import { useState, useEffect, useCallback, useMemo } from 'react';
import { validateRow, isRowFilled } from './bulkEntryValidation';

/**
 * Custom hook managing the entire bulk entry grid state.
 *
 * Grid state is stored as Record<source_id, RowFormData> where each row contains
 * string values for all input fields plus metadata (_existingEntryId, _editCount).
 *
 * @param {Array} sources - Array of source objects with source_id, name, needs_visit
 * @param {Array} entries - Array of existing pulse entries for the current date
 * @param {string} selectedGymId - Currently selected gym ID
 * @param {string} selectedPulseId - Currently selected pulse ID
 * @returns {{
 *   gridData: Object,
 *   gridErrors: Object,
 *   updateCell: Function,
 *   resetGrid: Function,
 *   filledSourceIds: Array,
 *   hasAnyErrors: boolean,
 *   submitProgress: { current: number, total: number },
 *   setSubmitProgress: Function
 * }}
 */
export function useBulkEntryState(sources, entries, selectedGymId, selectedPulseId) {
  const [gridData, setGridData] = useState({});
  const [gridErrors, setGridErrors] = useState({});
  const [submitProgress, setSubmitProgress] = useState({ current: 0, total: 0 });

  // Track a reset counter to force re-initialization
  const [resetCount, setResetCount] = useState(0);

  /**
   * Initialize grid when gym + pulse are both selected.
   * For each source, create a row keyed by source_id.
   * Pre-fill from existing entries if they match gym_id + pulse_id + source_id.
   */
  useEffect(() => {
    if (!selectedGymId || !selectedPulseId || !sources || sources.length === 0) {
      setGridData({});
      setGridErrors({});
      return;
    }

    const initialGrid = {};
    sources.forEach(source => {
      // Check if an entry already exists for this source + gym + pulse
      const existing = entries?.find(e =>
        e.gym_id === selectedGymId &&
        e.pulse_id === selectedPulseId &&
        e.source_id === source.source_id
      );

      if (existing) {
        // Pre-fill from existing entry (editable - will use PATCH on submit)
        initialGrid[source.source_id] = {
          dialed: String(existing.dialed || ''),
          dialed_company_ivr: String(existing.dialed_company_ivr || ''),
          dialed_fd_phone: String(existing.dialed_fd_phone || ''),
          connected: String(existing.connected || ''),
          visit_scheduled: String(existing.visit_scheduled || ''),
          visit_done: String(existing.visit_done || ''),
          closure_expected: String(existing.closure_expected || ''),
          revenue_expected: String(existing.revenue_expected || ''),
          closure_phone: String(existing.closure_phone || ''),
          closure_counter: String(existing.closure_counter || ''),
          comments: existing.comments || '',
          _existingEntryId: existing.id || existing._id || null,
          _editCount: existing.edit_count || 0,
        };
      } else {
        // Empty row for new entry
        initialGrid[source.source_id] = {
          dialed: '',
          dialed_company_ivr: '',
          dialed_fd_phone: '',
          connected: '',
          visit_scheduled: '',
          visit_done: '',
          closure_expected: '',
          revenue_expected: '',
          closure_phone: '',
          closure_counter: '',
          comments: '',
          _existingEntryId: null,
          _editCount: 0,
        };
      }
    });

    setGridData(initialGrid);
  }, [selectedGymId, selectedPulseId, sources, entries, resetCount]);

  /**
   * Update a single cell value.
   * Does NOT update metadata fields (those starting with '_').
   */
  const updateCell = useCallback((sourceId, field, value) => {
    if (field.startsWith('_')) return; // guard against metadata updates
    setGridData(prev => ({
      ...prev,
      [sourceId]: {
        ...prev[sourceId],
        [field]: value,
      },
    }));
  }, []);

  /**
   * Validate all filled rows whenever grid data changes.
   * Only validates rows where the user has entered at least one value.
   * Stores results as Record<source_id, Record<field, errorMessage>>.
   */
  useEffect(() => {
    if (!gridData || Object.keys(gridData).length === 0) {
      setGridErrors({});
      return;
    }

    const newErrors = {};
    Object.entries(gridData).forEach(([sourceId, rowData]) => {
      if (isRowFilled(rowData)) {
        // Look up whether this source needs visit
        const source = sources?.find(s => s.source_id === sourceId);
        const needsVisit = source?.needs_visit === 'Yes';
        newErrors[sourceId] = validateRow(rowData, needsVisit);
      } else {
        newErrors[sourceId] = {};
      }
    });

    setGridErrors(newErrors);
  }, [gridData, sources]);

  /**
   * Array of source_ids where the row has at least one non-empty, non-zero value.
   */
  const filledSourceIds = useMemo(() => {
    return Object.entries(gridData)
      .filter(([_, rowData]) => isRowFilled(rowData))
      .map(([sourceId]) => sourceId);
  }, [gridData]);

  /**
   * Boolean: true if any filled row has validation errors.
   */
  const hasAnyErrors = useMemo(() => {
    return Object.values(gridErrors).some(
      rowErrors => Object.keys(rowErrors).length > 0
    );
  }, [gridErrors]);

  /**
   * Reset the grid to empty state, which triggers re-initialization via useEffect.
   */
  const resetGrid = useCallback(() => {
    setResetCount(prev => prev + 1);
    setSubmitProgress({ current: 0, total: 0 });
  }, []);

  return {
    gridData,
    gridErrors,
    updateCell,
    resetGrid,
    filledSourceIds,
    hasAnyErrors,
    submitProgress,
    setSubmitProgress,
  };
}
